import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Percent, TrendingUp, AlertTriangle, Building2, FileSearch, X } from 'lucide-react';

/**
 * 관리자: 매칭 수수료 대시보드 (/admin/commissions)
 * - 월별 수수료 수입, 기공소별 청구/결제/연체 현황
 * - 플랫폼 사업자정보 설정 (config/platform — 수수료 세금계산서 공급자)
 * - 세금계산서 대사 리포트 (commissionAudits — 우회거래 감지)
 */
const STATUS_LABEL = {
    pending: { text: '대기', color: '#D97706', bg: '#FFFBEB' },
    paid: { text: '결제완료', color: '#059669', bg: '#ECFDF5' },
    failed: { text: '실패', color: '#DC2626', bg: '#FEF2F2' },
    overdue: { text: '연체', color: '#fff', bg: '#DC2626' },
    waived: { text: '면제', color: '#6B7280', bg: '#F3F4F6' },
};

const FLAG_LABEL = {
    no_statements: { text: '명세서 0건 (우회 의심)', color: '#DC2626' },
    tax_gt_statements: { text: '세금계산서 > 명세서 (누락 의심)', color: '#D97706' },
};

const fmt = (n) => (n || 0).toLocaleString('ko-KR');
const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #F3F4F6' };

function AdminCommissions() {
    const [invoices, setInvoices] = useState([]);
    const [names, setNames] = useState({});
    const [loading, setLoading] = useState(true);
    const [monthFilter, setMonthFilter] = useState('all');
    const [tab, setTab] = useState('invoices'); // invoices | audit
    const [audits, setAudits] = useState([]);
    const [showPlatformForm, setShowPlatformForm] = useState(false);
    const [platform, setPlatform] = useState({ businessNumber: '', businessName: '', representative: '', address: '', bizType: '', bizCategory: '', email: '' });
    const [savingPlatform, setSavingPlatform] = useState(false);

    const loadUserNames = useCallback(async (ids, prev) => {
        const result = { ...prev };
        await Promise.all([...new Set(ids)].filter(id => !result[id]).map(async id => {
            try {
                const u = await getDoc(doc(db, 'users', id));
                result[id] = u.exists() ? (u.data().businessName || u.data().name || id) : id;
            } catch (_) { result[id] = id; }
        }));
        return result;
    }, []);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [invSnap, auditSnap, platformSnap] = await Promise.all([
                    getDocs(collection(db, 'commissionInvoices')),
                    getDocs(collection(db, 'commissionAudits')),
                    getDoc(doc(db, 'config', 'platform')),
                ]);
                const list = invSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                list.sort((a, b) => (b.month || '').localeCompare(a.month || ''));
                setInvoices(list);

                const auditList = auditSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                auditList.sort((a, b) => (b.month || '').localeCompare(a.month || ''));
                setAudits(auditList);

                if (platformSnap.exists()) setPlatform(prev => ({ ...prev, ...platformSnap.data() }));

                const ids = [
                    ...list.map(i => i.labId),
                    ...auditList.flatMap(a => (a.rows || []).flatMap(r => [r.labId, r.clinicId])),
                ];
                setNames(await loadUserNames(ids, {}));
            } catch (e) {
                console.error('수수료 현황 조회 실패:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [loadUserNames]);

    const savePlatform = async () => {
        if (!platform.businessNumber || !platform.businessName) {
            alert('사업자번호와 상호는 필수입니다.'); return;
        }
        setSavingPlatform(true);
        try {
            await setDoc(doc(db, 'config', 'platform'), { ...platform, updatedAt: serverTimestamp() }, { merge: true });
            alert('저장되었습니다. 이후 결제되는 수수료부터 이 정보로 세금계산서가 자동 발행됩니다.');
            setShowPlatformForm(false);
        } catch (e) {
            alert('저장 실패: ' + e.message);
        } finally {
            setSavingPlatform(false);
        }
    };

    const months = [...new Set(invoices.map(i => i.month))];
    const filtered = monthFilter === 'all' ? invoices : invoices.filter(i => i.month === monthFilter);
    const totalPaid = filtered.filter(i => i.status === 'paid').reduce((s, i) => s + (i.totalAmount || 0), 0);
    const totalPendingAmt = filtered.filter(i => ['pending', 'failed', 'overdue'].includes(i.status)).reduce((s, i) => s + (i.totalAmount || 0), 0);
    const overdueCount = filtered.filter(i => i.status === 'overdue').length;

    const statCard = (label, value, color, Icon) => (
        <div style={{ flex: 1, minWidth: 160, border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, background: '#fff' }}>
            <div style={{ fontSize: 13, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 6 }}><Icon size={15} /> {label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color, marginTop: 6 }}>{value}</div>
        </div>
    );

    const tabBtn = (key, label, Icon) => (
        <button onClick={() => setTab(key)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20, fontSize: 14, fontWeight: 600, cursor: 'pointer', border: tab === key ? '1px solid #2563EB' : '1px solid #E5E7EB', background: tab === key ? '#EFF6FF' : '#fff', color: tab === key ? '#2563EB' : '#6B7280' }}>
            <Icon size={15} /> {label}
        </button>
    );

    return (
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <h1 style={{ fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
                    <Percent size={24} color="#2563EB" /> 매칭 수수료 관리
                </h1>
                <button onClick={() => setShowPlatformForm(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #D1D5DB', padding: '9px 14px', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                    <Building2 size={15} /> 플랫폼 사업자정보 {platform.businessNumber ? '수정' : '설정 (필수)'}
                </button>
            </div>

            {!platform.businessNumber && !loading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFFBEB', color: '#D97706', padding: '12px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600, margin: '14px 0 0' }}>
                    <AlertTriangle size={18} /> 플랫폼 사업자정보가 설정되지 않아 수수료 세금계산서 자동발행이 보류됩니다.
                </div>
            )}

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '20px 0' }}>
                {statCard('결제 완료 수입', `${fmt(totalPaid)}원`, '#059669', TrendingUp)}
                {statCard('미수금 (대기+실패+연체)', `${fmt(totalPendingAmt)}원`, '#D97706', Percent)}
                {statCard('연체 기공소', `${overdueCount}곳`, '#DC2626', AlertTriangle)}
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                {tabBtn('invoices', '청구 현황', Percent)}
                {tabBtn('audit', '대사 리포트', FileSearch)}
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF' }}>불러오는 중...</div>
            ) : tab === 'invoices' ? (
                <>
                    <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
                        style={{ padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, marginBottom: 16 }}>
                        <option value="all">전체 월</option>
                        {months.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: 12 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, background: '#fff' }}>
                            <thead>
                                <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                                    {['월', '기공소', '기준액', '수수료', 'VAT', '청구액', '상태', '세금계산서', '시도'].map(h => (
                                        <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid #E5E7EB', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(inv => {
                                    const s = STATUS_LABEL[inv.status] || STATUS_LABEL.pending;
                                    return (
                                        <tr key={inv.id}>
                                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{inv.month}</td>
                                            <td style={tdStyle}>{names[inv.labId] || inv.labId}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(inv.baseAmount)}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(inv.commissionAmount)}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(inv.vat)}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(inv.totalAmount)}</td>
                                            <td style={tdStyle}>
                                                <span style={{ fontSize: 12, fontWeight: 700, color: s.color, background: s.bg, padding: '3px 8px', borderRadius: 10 }}>{s.text}</span>
                                            </td>
                                            <td style={tdStyle}>
                                                {inv.taxInvoiceStatus === 'issued' ? <span style={{ color: '#059669', fontSize: 13 }}>발행완료</span>
                                                    : inv.taxInvoiceStatus === 'error' ? <span style={{ color: '#DC2626', fontSize: 13 }} title={inv.taxInvoiceError}>오류</span>
                                                        : <span style={{ color: '#9CA3AF', fontSize: 13 }}>-</span>}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>{inv.attemptCount || 0}</td>
                                        </tr>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>청구 내역이 없습니다.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {audits.length === 0 && (
                        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF', background: '#F9FAFB', borderRadius: 12 }}>
                            대사 리포트가 아직 없습니다. 매월 11일에 자동 생성됩니다.
                        </div>
                    )}
                    {audits.map(a => (
                        <div key={a.id} style={{ border: '1px solid #E5E7EB', borderRadius: 12, background: '#fff', padding: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                                <span style={{ fontWeight: 700, fontSize: 16 }}>{a.month}</span>
                                <span style={{ fontSize: 13, color: a.flaggedCount > 0 ? '#DC2626' : '#059669', fontWeight: 600 }}>
                                    파트너십 {a.partnershipCount}건 중 의심 {a.flaggedCount}건
                                </span>
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                    <thead>
                                        <tr style={{ background: '#F9FAFB', textAlign: 'left' }}>
                                            {['기공소', '치과', '명세서', '명세서 합계', '세금계산서 합계', '플래그'].map(h => (
                                                <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid #E5E7EB', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(a.rows || []).map((r, i) => (
                                            <tr key={i} style={{ background: r.flags?.length ? '#FEF2F2' : 'transparent' }}>
                                                <td style={tdStyle}>{names[r.labId] || r.labId}</td>
                                                <td style={tdStyle}>{names[r.clinicId] || r.clinicId}</td>
                                                <td style={{ ...tdStyle, textAlign: 'center' }}>{r.statementCount}건</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.statementTotal)}원</td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.taxTotal)}원</td>
                                                <td style={tdStyle}>
                                                    {(r.flags || []).map(f => (
                                                        <span key={f} style={{ display: 'inline-block', fontSize: 12, fontWeight: 600, color: FLAG_LABEL[f]?.color, marginRight: 8 }}>
                                                            {FLAG_LABEL[f]?.text || f}
                                                        </span>
                                                    ))}
                                                    {(!r.flags || r.flags.length === 0) && <span style={{ color: '#059669', fontSize: 12 }}>정상</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* 플랫폼 사업자정보 설정 모달 */}
            {showPlatformForm && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 440, width: '92%', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>플랫폼(덴탈클러스터) 사업자정보</h3>
                            <button onClick={() => setShowPlatformForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}><X size={20} /></button>
                        </div>
                        <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 16px' }}>수수료 세금계산서(덴탈클러스터→기공소)의 공급자 정보로 사용됩니다.</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {[
                                ['businessNumber', '사업자번호 *', '000-00-00000'],
                                ['businessName', '상호 *', '덴탈클러스터'],
                                ['representative', '대표자명', ''],
                                ['address', '사업장 주소', ''],
                                ['bizType', '업태', '서비스업'],
                                ['bizCategory', '종목', '플랫폼 중개'],
                                ['email', '이메일', ''],
                            ].map(([key, label, ph]) => (
                                <div key={key}>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#374151' }}>{label}</label>
                                    <input value={platform[key] || ''} placeholder={ph}
                                        onChange={e => setPlatform(prev => ({ ...prev, [key]: e.target.value }))} style={inputStyle} />
                                </div>
                            ))}
                        </div>
                        <button onClick={savePlatform} disabled={savingPlatform}
                            style={{ width: '100%', marginTop: 18, background: '#2563EB', color: '#fff', border: 'none', padding: '12px', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                            {savingPlatform ? '저장 중...' : '저장'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminCommissions;
